'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["slots", "slotProps", "className", "classes"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageAvatar } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageAvatar');
const ChatMessageAvatarStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'Avatar',
  overridesResolver: (_, styles) => styles.avatar
})(({
  theme,
  ownerState
}) => _extends({
  gridArea: 'avatar',
  width: 'var(--MuiChatMessage-avatarSize, 36px)',
  height: 'var(--MuiChatMessage-avatarSize, 36px)',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  alignSelf: 'flex-start',
  backgroundColor: (theme.vars || theme).palette.grey[300],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.secondary,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}, ownerState?.variant === 'compact' && {
  gridRow: '1 / 3',
  alignSelf: 'flex-start',
  marginTop: theme.spacing(0.25)
}));
const ChatMessageAvatar = /*#__PURE__*/React.forwardRef(function ChatMessageAvatar(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageAvatar'
  });
  // Drop a JS/theme-injected `classes` (not a prop on this sub-part — it shares
  // the `MuiChatMessage-*` namespace) so it can't leak onto the DOM via `...other`.
  const _ref = props,
    {
      slots,
      slotProps,
      className,
      classes: classesProp
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  void classesProp;
  const classes = useChatMessageUtilityClasses(undefined);
  return /*#__PURE__*/_jsx(MessageAvatar, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      avatar: slots?.avatar ?? ChatMessageAvatarStyled
    }),
    slotProps: _extends({}, slotProps, {
      avatar: mergeSlotProps({
        className: clsx(classes.avatar, className)
      }, slotProps?.avatar)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageAvatar.displayName = "ChatMessageAvatar";
process.env.NODE_ENV !== "production" ? ChatMessageAvatar.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  className: PropTypes.string,
  slotProps: PropTypes.object,
  slots: PropTypes.object
} : void 0;
export { ChatMessageAvatar };